import { TableWrapperStack } from '@common/components';
import { EntityNotFound } from '@common/EntityNotFound';
import { PageHeader } from '@common/Header/PageHeader';
import { TeamInfoWithAdmin, TeamWithAdminQuery } from '@common/queries/team';
import { useHasTeamAdminAccess } from '@common/teams/hooks';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { useIsUserTeamAdmin } from '@common/useUserType';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import {
  Centered,
  Container,
  LabelWithHint,
  Spinner,
  Stack,
  StackItem,
  TAB_CONTENT_WRAPPER_CLASS_NAME,
  TabPane,
  Tabs
} from '@prophecy/ui-v3';
import { DefaultRecordType } from '@prophecy/ui-v3/Table/types';
import { Alert } from '@prophecy/ui-v3/ui-compat';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { useIsSparkSupported, useEditionConfig } from 'frontend/core/src/context/edition';
import { lowerCase } from 'lodash-es';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMixPanel } from '../../../context/mixpanel/context';
import { useGraphQlQuery } from '../../../data/util';
import CodeGenerationTab from '../../Team/components/CodegenTab';
import { ExecutionMetricsTab } from '../../Team/components/ExecutionMetricsTab';
import { TemplateCreation } from '../../UserSettings/Admin/TeamplateCreation';
import { useEntityTableSchema, getEntityTableSortConfigVariables } from '../components/Config';
import { EntityTable } from '../components/EntityTable';
import { TeamPipelines } from '../components/pipeline-list';
import { TeamInfoEntity } from '../components/Teams/TeamInfo';
import { Users } from '../components/Users';
import { SchemaType } from '../config/types';
import { ArtefactForm } from './ProjectDetail/Components/ArtefactForm';
import CopilotSettings from './ProjectDetail/Components/Copilot';
import { DefaultProjects } from './ProjectDetail/DefaultProjects';
const StyledTabs = styled(Tabs)`
  .${TAB_CONTENT_WRAPPER_CLASS_NAME} {
    overflow: hidden;
  }
`;
const EntityTableTabs = {
  firstTab: 'info',
  tabs: [
    {
      key: `${lowerCase(Entity.Project)}s`,
      title: `${Entity.Project}s`,
      entity: Entity.Project
    },
    {
      key: `${lowerCase(Entity.Pipeline)}s`,
      title: `${Entity.Pipeline}s`,
      entity: Entity.Pipeline
    },
    {
      key: `${lowerCase(Entity.Job)}s`,
      title: `${Entity.Job}s`,
      entity: Entity.Job
    },
    {
      key: `${lowerCase(Entity.Dataset)}s`,
      title: `${Entity.Dataset}s`,
      entity: Entity.Dataset
    },
    {
      key: `${lowerCase(Entity.Subgraph)}s`,
      title: `${Entity.Subgraph}s`,
      entity: Entity.Subgraph
    },
    {
      key: `${lowerCase(Entity.Member)}s`,
      title: `${Entity.Member}s`,
      entity: Entity.Member
    },
    {
      key: `${lowerCase(Entity.Fabric)}s`,
      title: `${Entity.Fabric}s`,
      entity: Entity.Fabric
    }
  ]
} as const;

const TeamEntityDetail = ({ entity, parentEntity, uid }: { entity: Entity; parentEntity: Entity; uid: string }) => {
  const tableSchema = useEntityTableSchema(parentEntity, entity) as SchemaType<DefaultRecordType>;
  return (
    <EntityTable
      schema={tableSchema}
      variables={{ uid }}
      // @ts-ignore
      sortConfigVariables={getEntityTableSortConfigVariables(entity)}
    />
  );
};

export default function Page() {
  const entity = Entity.Team;
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.Team.tab.url>>();

  const tab = params.tab || EntityTableTabs.firstTab;
  const uid = decodeURIComponent(params.uid as string);
  const variables = { uid };
  const { data, isLoading } = useGraphQlQuery(
    [entity, entity + 'Detail', variables],
    TeamWithAdminQuery,
    undefined,
    variables
  );
  const team = data?.Team;
  if (isLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  if (!team) return <EntityNotFound uid={uid} entity={Entity.Team} projectContext={false} />;
  return <Team uid={uid} team={team} tab={tab} />;
}

function Team({ uid, team: currentData, tab }: { uid: string; team: TeamInfoWithAdmin; tab: string }) {
  const entity = Entity.Team;
  const hasTeamAdminAccess = useHasTeamAdminAccess();
  const isAdmin = useIsUserTeamAdmin(uid) || hasTeamAdminAccess;

  const { track } = useMixPanel();
  const editionConfig = useEditionConfig();
  const isSparkSupported = useIsSparkSupported();

  const isTabValid = (tab: (typeof EntityTableTabs.tabs)[number]) => {
    if (!isSparkSupported && tab.entity === Entity.Job) {
      return false;
    }

    if (!isSparkSupported && tab.entity === Entity.Dataset) {
      return false;
    }

    if (!editionConfig.isSubgraphEnabled && tab.entity === Entity.Subgraph) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    track(EntityEvents.common.metadata.entityOpen, {
      uid,
      name: currentData.name,
      entity,
      url: window.location.href
    });
  }, [track, entity, currentData.name, uid]);

  const changeTab = (tab: string) => {
    track(EntityEvents.common.metadata.tabChange, {
      uid,
      name: currentData?.name,
      tab,
      entity,
      url: window.location.href
    });
  };

  const { globalTeamName } = useEditionConfig();
  const isGlobalTeam = currentData?.name === globalTeamName;

  const getTabUrl = (key: string) => Private_Routes.Team.tab.getUrl({ uid, tab: key });

  const showSettingsTab = isSparkSupported || editionConfig.isProjectTemplateEnabled;

  return (
    <Stack width='100%' height='100%'>
      <DocumentTitle title={`${entity}:${currentData?.name || ''}`} />
      <PageHeader
        crumbs={[
          { label: currentData?.name || 'Team', url: Private_Routes.Team.home.getUrl({ uid }), category: Entity.Team }
        ]}
      />
      {isGlobalTeam && (
        <Alert variant='warning' onClose={() => {}}>
          <span>
            This is a system-level team used for Prophecy execution connectivity. Please do not create or modify
            projects in this team. If you create any projects in this team, expect it to be visible/accessible by your
            other environments
          </span>
        </Alert>
      )}
      <TableWrapperStack width='100%' height='100%' align='center' alignY='center'>
        <Container>
          <StyledTabs defaultActiveKey={tab} onChange={changeTab}>
            <TabPane
              key={EntityTableTabs.firstTab}
              to={getTabUrl(EntityTableTabs.firstTab)}
              tab='Info'
              elementType='link'>
              <TeamInfoEntity teamId={uid} />;
            </TabPane>
            {EntityTableTabs.tabs.map((tab) => {
              if (!isTabValid(tab)) {
                return null;
              }
              return (
                <TabPane key={tab.key} to={getTabUrl(tab.key)} tab={tab.title} elementType='link'>
                  {tab.key === 'members' ? (
                    <Users teamId={uid} />
                  ) : tab.key === `${lowerCase(Entity.Pipeline)}s` ? (
                    <TeamPipelines teamId={uid} />
                  ) : (
                    <TeamEntityDetail uid={uid} entity={tab.entity} parentEntity={entity} />
                  )}
                </TabPane>
              );
            })}
            {showSettingsTab && (
              <TabPane key='advanced' to={getTabUrl('advanced')} tab='Settings' elementType='link'>
                <Tabs defaultActiveKey={isAdmin && isSparkSupported ? 'executionMetrics' : 'defaultProjectSettings'}>
                  {isSparkSupported && (
                    <TabPane key='executionMetrics' tab='Execution Metrics' disabled={!isAdmin}>
                      {isAdmin ? <ExecutionMetricsTab teamId={uid} data={currentData} /> : null}
                    </TabPane>
                  )}
                  {isSparkSupported && (
                    <TabPane key='codegen' tab='Code Generation'>
                      <CodeGenerationTab team={currentData} />
                    </TabPane>
                  )}
                  {isSparkSupported && (
                    <TabPane key='setting' tab='Advanced'>
                      <Stack gap={theme.spaces.x32} height='100%'>
                        <ArtefactForm
                          label={
                            <LabelWithHint
                              hint='Update default artifact Id for team. New projects will be initialized with this value as parent
                Artifact Id. Existing ones will not be impacted.'
                              level='sm13'>
                              Artifact Id
                            </LabelWithHint>
                          }
                          uid={uid}
                          isReadMode={!isAdmin}
                          entity={Entity.Team}
                          key={currentData?.artefactID}
                          artefactID={currentData?.artefactID as string}
                        />
                        {isAdmin ? (
                          <StackItem>
                            <CopilotSettings {...currentData} teamId={uid} />
                          </StackItem>
                        ) : null}
                        {isAdmin && isSparkSupported ? (
                          <StackItem grow='1'>
                            <DefaultProjects teamId={uid} />
                          </StackItem>
                        ) : null}
                      </Stack>
                    </TabPane>
                  )}
                  {editionConfig.isProjectTemplateEnabled && (
                    <TabPane disabled={!isAdmin} key='defaultProjectSettings' tab='Default Project Settings'>
                      <TemplateCreation teamId={uid} />
                    </TabPane>
                  )}
                </Tabs>
              </TabPane>
            )}
          </StyledTabs>
        </Container>
      </TableWrapperStack>
    </Stack>
  );
}
